/**
 * React Toast Service
 *
 * This service replaces the Angular toastr functionality from:
 * - app/configuration/toastr.configuration.js (configuration)
 * - app/factory/toast.factory.js (factory implementation)
 * - app/template/toastr/toast.html (template)
 *
 * Angular Configuration Equivalent:
 * - extendedTimeOut: 4000ms
 * - maxOpened: 3
 * - positionClass: 'toast-top-right'
 * - progressBar: false
 * - tapToDismiss: true
 * - closeButton: true
 * - timeOut: 4000ms
 */

import i18n from './i18n/i18n'

interface ToastConfig {
  timeOut: number
  extendedTimeOut: number
  maxOpened: number
  positionClass: string
  tapToDismiss: boolean
  closeButton: boolean
}

interface ToastElement extends HTMLElement {
  timer?: number
  closeTimer?: number
}

class ToastService {
  private readonly config: ToastConfig = {
    timeOut: 4000,
    extendedTimeOut: 4000,
    maxOpened: 3,
    positionClass: 'toast-top-right',
    tapToDismiss: true,
    closeButton: true
  }

  private readonly activeToasts: ToastElement[] = []
  private container: HTMLElement | null = null

  /**
   * Translate a message key to localized text
   * @param message - Message key or plain text
   * @returns Translated message
   */
  private translate(message: string): string {
    if (!i18n.exists(message)) {
      // If the key doesn't exist in translations, return the message as-is (might be plain text)
      return message
    }
    const result = i18n.t(message)
    return typeof result === 'string' ? result : String(result)
  }

  private getOrCreateContainer(): HTMLElement {
    if (!this.container || !document.body.contains(this.container)) {
      this.container = document.getElementById('toast-container')

      if (!this.container) {
        this.container = document.createElement('div')
        this.container.id = 'toast-container'
        this.container.className = this.config.positionClass
        document.body.appendChild(this.container)
      }
    }

    return this.container
  }

  private createToastElement(message: string, type: 'success' | 'error' | 'warning' | 'info'): ToastElement {
    const toastEl = document.createElement('div') as ToastElement

    // Use legacy CSS classes to match old app styling
    toastEl.className = `toast toast-${type}`

    // Create message container using legacy class
    const messageDiv = document.createElement('div')
    messageDiv.className = 'toast-message'
    messageDiv.setAttribute('aria-label', message)
    messageDiv.textContent = message
    toastEl.appendChild(messageDiv)

    // Add close button if configured (matches legacy structure)
    if (this.config.closeButton) {
      const closeButton = document.createElement('button')
      closeButton.className = 'toast-close-button'
      closeButton.setAttribute('aria-label', 'Close')
      closeButton.onclick = (e) => {
        e.stopPropagation()
        this.removeToast(toastEl)
      }
      toastEl.appendChild(closeButton)
    }

    // Add tap to dismiss if configured
    if (this.config.tapToDismiss) {
      toastEl.onclick = () => this.removeToast(toastEl)
    }

    return toastEl
  }

  private removeToast(toastEl: ToastElement): void {
    if (toastEl.timer) {
      clearTimeout(toastEl.timer)
    }
    if (toastEl.closeTimer) {
      clearTimeout(toastEl.closeTimer)
    }

    const index = this.activeToasts.indexOf(toastEl)
    if (index > -1) {
      this.activeToasts.splice(index, 1)
      if (toastEl.parentNode) {
        toastEl.remove()
      }
    }
  }

  private showToast(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    // Translate the message if it's a translation key
    const translatedMessage = this.translate(message)

    // Enforce max opened toasts
    if (this.activeToasts.length >= this.config.maxOpened) {
      this.removeToast(this.activeToasts[0])
    }

    const container = this.getOrCreateContainer()
    const toastEl = this.createToastElement(translatedMessage, type)

    container.appendChild(toastEl)
    this.activeToasts.push(toastEl)

    toastEl.timer = window.setTimeout(() => {
      this.removeToast(toastEl)
    }, this.config.timeOut)

    // Extended timeout on hover
    toastEl.onmouseenter = () => {
      if (toastEl.timer) {
        clearTimeout(toastEl.timer)
      }
    }

    toastEl.onmouseleave = () => {
      toastEl.closeTimer = window.setTimeout(() => {
        this.removeToast(toastEl)
      }, this.config.extendedTimeOut)
    }
  }

  /**
   * Display a success toast message
   * Replaces: Toast.success() from toast.factory.js
   */
  success(message: string): void {
    this.showToast(message, 'success')
  }

  /**
   * Display an error toast message
   * Replaces: Toast.error() from toast.factory.js
   */
  error(message: string): void {
    this.showToast(message, 'error')
  }

  /**
   * Display a warning toast message
   * Replaces: Toast.warning() from toast.factory.js
   */
  warning(message: string): void {
    this.showToast(message, 'warning')
  }

  /**
   * Display an info toast message
   * Replaces: Toast.info() from toast.factory.js
   */
  info(message: string): void {
    this.showToast(message, 'info')
  }

  /**
   * Clear all active toasts or a specific toast
   * Replaces: Toast.clear() from toast.factory.js
   */
  clear(toastEl?: ToastElement): void {
    if (toastEl) {
      this.removeToast(toastEl)
    } else {
      // Clear all toasts
      ;[...this.activeToasts].forEach((toast) => this.removeToast(toast))
    }
  }

  /**
   * Refresh timer for a specific toast
   * Replaces: Toast.refreshTimer() from toast.factory.js
   */
  refreshTimer(toastEl: ToastElement, time?: number): void {
    if (toastEl.timer) {
      clearTimeout(toastEl.timer)
    }
    if (toastEl.closeTimer) {
      clearTimeout(toastEl.closeTimer)
    }

    const timeout = time ?? this.config.timeOut
    toastEl.timer = window.setTimeout(() => {
      this.removeToast(toastEl)
    }, timeout)
  }
}

// Export singleton instance to match Angular factory pattern
export const toast = new ToastService()
